import { useState } from "react";
import { Button, Card, CardBody, Col, Input, Row } from "reactstrap";
import {
  LuCheck,
  LuMinus,
  LuPlus,
  LuZap
} from "react-icons/lu";
import { GoShieldCheck } from "react-icons/go";
import { useTranslation } from "react-i18next";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import './styles.css';
import { Link } from "react-router-dom";
import { BILLING_PRICES } from "../../../../utils/prices";
import { MODULES } from "../../../../utils/modules";

const HIGHLIGHT_KEYS = [
  "billing.highlightStorage",
  "billing.highlightSupport",
  "billing.highlightOnboarding",
  "billing.highlightUpdates",
];

function SubscribePage() {
  const { t } = useTranslation();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [userCount, setUserCount] = useState(2);
  const [currency, setCurrency] = useState("usd");

  const price = BILLING_PRICES[currency];
  const perUserPrice = price[billingCycle];
  const totalPrice = perUserPrice * userCount;
  const cycleLabel = billingCycle === "monthly" ? t('billing.moAbbr') : t('billing.yrAbbr');
  const sliderFill = `${((userCount - 1) / 99) * 100}%`;

  function handleUserCount(val) {
    const clamped = Math.min(100, Math.max(1, val));
    setUserCount(clamped);
  }

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('billing.subscribe')}>
          <Input
            type="select"
            name="currency"
            className="px-3"
            style={{ maxWidth: 110, borderRadius: 8, fontSize: "0.875rem" }}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            <option value="usd">USD</option>
            <option value="eur">EUR</option>
            <option value="dzd">DZD</option>
            <option value="kwd">KWD</option>
            <option value="sar">SAR</option>
          </Input>
        </PageSubHeader>

        <div className="subheader-page-content scrollable">
          {/* Billing cycle toggle */}
          <div className="d-flex justify-content-center mb-4">
            <div className="billing-toggle">
              <button
                className={`billing-toggle-btn${billingCycle === "monthly" ? " active" : ""}`}
                onClick={() => setBillingCycle("monthly")}
              >
                {t('billing.monthly')}
              </button>
              <button
                className={`billing-toggle-btn${billingCycle === "yearly" ? " active" : ""}`}
                onClick={() => setBillingCycle("yearly")}
              >
                {t('billing.yearly')}&nbsp;
                <span
                  style={{
                    background: billingCycle === "yearly" ? "rgba(255,255,255,0.25)" : "rgba(34,197,94,0.15)",
                    color: billingCycle === "yearly" ? "#fff" : "#22c55e",
                    borderRadius: 50,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    padding: "1px 7px",
                  }}
                >
                  {t('billing.save17')}
                </span>
              </button>
            </div>
          </div>

          <Row className="g-4 align-items-start">
            {/* Pricing card */}
            <Col lg={5}>
              <Card>
                <CardBody style={{ padding: "1.75rem" }}>
                  {/* Plan name */}
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="billing-plan-badge">
                      <LuZap size={13} />
                      {t('billing.nazarPro')}
                    </div>
                    <span className="text-muted" style={{ fontSize: "0.8125rem" }}>
                      {t('billing.allModulesIncluded')}
                    </span>
                  </div>

                  {/* Per-user price */}
                  <div className="d-flex align-items-end gap-2 mb-1">
                    <span className="billing-price-amount">
                      {price.symbol}{perUserPrice.toLocaleString()}
                    </span>
                    <div className="billing-price-unit mb-1">
                      {t('billing.perUser')}<br />
                      {billingCycle === "monthly" ? t('billing.perMonthLabel') : t('billing.perYearLabel')}
                    </div>
                  </div>

                  {billingCycle === "yearly" && (
                    <p className="text-muted mb-4" style={{ fontSize: "0.8125rem" }}>
                      {t('billing.billedAsInsteadOf', {
                        billedAmount: `${price.symbol}${(BILLING_PRICES[currency].monthly * 10).toLocaleString()}`,
                        fullAmount: `${price.symbol}${(BILLING_PRICES[currency].monthly * 12).toLocaleString()}`,
                      })}
                    </p>
                  )}
                  {billingCycle === "monthly" && <div className="mb-4" />}

                  {/* Divider */}
                  <hr style={{ borderColor: "rgba(0,0,0,0.07)", margin: "0 0 1.25rem" }} />

                  {/* User count */}
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1d1d1f" }}>
                      {t('billing.numberOfUsers')}
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        className="billing-user-count-btn"
                        onClick={() => handleUserCount(userCount - 1)}
                        disabled={userCount <= 1}
                      >
                        <LuMinus size={13} />
                      </button>
                      <span style={{ fontWeight: 700, fontSize: "1rem", minWidth: 28, textAlign: "center" }}>
                        {userCount}
                      </span>
                      <button
                        className="billing-user-count-btn"
                        onClick={() => handleUserCount(userCount + 1)}
                        disabled={userCount >= 100}
                      >
                        <LuPlus size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Slider */}
                  <input
                    type="range"
                    min={1}
                    max={100}
                    value={userCount}
                    onChange={(e) => handleUserCount(Number(e.target.value))}
                    className="billing-slider mb-1"
                    style={{
                      background: `linear-gradient(to right, #6C63FF 0%, #6C63FF ${sliderFill}, rgba(108,99,255,0.15) ${sliderFill}, rgba(108,99,255,0.15) 100%)`,
                    }}
                  />
                  <div className="d-flex justify-content-between mb-4">
                    <span style={{ fontSize: "0.75rem", color: "#6e6e73" }}>{t('billing.oneUser')}</span>
                    <span style={{ fontSize: "0.75rem", color: "#6e6e73" }}>{t('billing.hundredUsers')}</span>
                  </div>

                  {/* Total bar */}
                  <div className="billing-total-bar d-flex align-items-center justify-content-between mb-4">
                    <span style={{ fontSize: "0.875rem", color: "#6e6e73" }}>
                      {t('billing.userCountShort', { count: userCount })} × {price.symbol}{perUserPrice.toLocaleString()} / {cycleLabel}
                    </span>
                    <span style={{ fontWeight: 700, fontSize: "1rem", color: "#6C63FF" }}>
                      {price.symbol}{totalPrice.toLocaleString()}{" "}
                      <span style={{ fontWeight: 400, fontSize: "0.8125rem", color: "#6e6e73" }}>/ {cycleLabel}</span>
                    </span>
                  </div>

                  {/* Highlights */}
                  <div className="d-flex flex-column gap-2 mb-4">
                    {HIGHLIGHT_KEYS.map((key) => (
                      <div className="billing-highlight-item" key={key}>
                        <div className="billing-highlight-check">
                          <LuCheck size={10} strokeWidth={3} />
                        </div>
                        {t(key)}
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Link
                    to="/settings/billing/pay"
                    className="btn btn-primary btn-lg w-100"
                    state={{ billingCycle, userCount, currency }}
                  >
                    {t('billing.subscribeNowCta', { price: `${price.symbol}${totalPrice.toLocaleString()}`, cycle: cycleLabel })}
                  </Link>

                  <p className="text-center text-muted mt-3 mb-0" style={{ fontSize: "0.75rem" }}>
                    <GoShieldCheck size={13} className="me-1" />
                    {t('billing.subscribeFooterNote')}
                  </p>
                </CardBody>
              </Card>
            </Col>

            {/* Modules included card */}
            <Col lg={7}>
              <Card>
                <CardBody style={{ padding: "1.75rem" }}>
                  <p className="text-muted small fw-semibold text-uppercase mb-1" style={{ letterSpacing: "0.06em" }}>
                    {t('billing.whatsIncluded')}
                  </p>
                  <p className="mb-4" style={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1d1d1f" }}>
                    {t('billing.allModulesSubtitle')}
                  </p>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                      gap: "0.625rem",
                    }}
                  >
                    {MODULES.map((mod) => {
                      const Icon = mod.icon;
                      return (
                        <div className="billing-module-card" key={mod.name}>
                          <div className="billing-module-icon" style={{ background: mod.bg, color: mod.color }}>
                            <Icon size={18} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: "0.875rem", color: "#1d1d1f", lineHeight: 1.2 }}>
                              {mod.name}
                            </div>
                            <div style={{ fontSize: "0.75rem", color: "#6e6e73", lineHeight: 1.4 }}>
                              {mod.description}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Bottom note */}
                  <div
                    className="mt-4 d-flex align-items-start gap-3 p-3"
                    style={{
                      background: "rgba(108,99,255,0.05)",
                      border: "1px solid rgba(108,99,255,0.12)",
                      borderRadius: 12,
                    }}
                  >
                    <div
                      style={{
                        background: "rgba(108,99,255,0.1)",
                        color: "#6C63FF",
                        borderRadius: 8,
                        width: 36,
                        height: 36,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <LuZap size={16} />
                    </div>
                    <div>
                      <p className="mb-0 fw-semibold" style={{ fontSize: "0.875rem", color: "#1d1d1f" }}>
                        {t('billing.everythingNeedTitle')}
                      </p>
                      <p className="mb-0 text-muted" style={{ fontSize: "0.8125rem" }}>
                        {t('billing.everythingNeedDesc')}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </SidebarLayout>
  );
}

export default SubscribePage;
